import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { useAuthStore } from "@/store/authStore";
import { demoWorkLogs } from "@/data/demoData";

/**
 * A submission as the review screen sees it.
 *
 * The description and challenges are typed explicitly because this screen used
 * to read `log.description` from an untyped payload that only carried `content`,
 * which is how it ended up showing an empty task box for every submission.
 */
export interface PartnerWorkLog {
  id: number | string;
  date: string;
  hours: number;
  description?: string;
  content?: string;
  challenges?: string;
  challenge?: string;
  status: string;
  feedback?: string;
  student_name?: string;
  studentName?: string;
  student_university?: string;
  studentUniversity?: string;
  student_major?: string;
  studentMajor?: string;
  student_year?: number;
  studentYear?: number;
  student_approved_logs?: number;
  studentApprovedLogs?: number;
  student_reviewed_logs?: number;
  studentReviewedLogs?: number;
  placement_title?: string;
  created_at?: string;
}

export function usePartnerWorkLog(id: string) {
  const { user } = useAuthStore();

  return useQuery<PartnerWorkLog | null>({
    queryKey: ["partner-work-log", id],
    queryFn: async (): Promise<PartnerWorkLog | null> => {
      if (user?.isDemo) {
        const found = demoWorkLogs.find((l) => String(l.id) === String(id)) ?? demoWorkLogs[0];
        return (found as unknown as PartnerWorkLog) ?? null;
      }
      const res = await apiClient.get(`/partners/me/work-logs/${id}`);
      return (res.data.data as PartnerWorkLog) ?? null;
    },
    enabled: !!id,
  });
}

export function useReviewWorkLog() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async ({
      id,
      status,
      feedback,
    }: {
      id: string;
      status: string;
      feedback: string;
    }) => {
      if (user?.isDemo) {
        return { success: true, message: "Review submitted successfully (Demo Mode)" };
      }
      const res = await apiClient.post(`/partners/me/work-logs/${id}/review`, { status, feedback });
      return res.data.data;
    },
    onSuccess: (_data, variables) => {
      if (user?.isDemo) {
        queryClient.setQueryData(["partner-work-log", variables.id], (old: any) =>
          old ? { ...old, status: variables.status, feedback: variables.feedback } : old
        );
        queryClient.setQueryData(["partner-work-logs"], (old: any[]) =>
          old?.map((l: any) =>
            String(l.id) === String(variables.id) ? { ...l, status: variables.status, feedback: variables.feedback } : l
          )
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ["partner-work-log", variables.id] });
        queryClient.invalidateQueries({ queryKey: ["partner-work-logs"] });
        queryClient.invalidateQueries({ queryKey: ["partner-dashboard"] });
      }
    },
  });
}
